import { WebTokenType } from '@ps/platform-message-framework/webtokenserver/WebTokenType';
import { Currency, FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { PlayMoneyRefillInfo } from '@ps/web-shared-api-types/v0/RAM2';
import { RawButton, StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { encodeQuery } from '@swf/core/utils';

// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../Header.scss';

import {
  TXTCLI_Buy_Chips,
  TXTWCL_Collect_Chips,
  TXTWCL_YouXve_reached_the_maximum_amo_ELL,
  TXTWCL_You_can_collect_up_to_N0_chips_ELL,
  TXTWCL_You_can_collect_up_to_N0_chips_ELL_1,
  TXTWCL_You_can_collect_up_to_N0_chips_ELL_2
} from './PlayMoney.messages.json';
import { playChipsMessage } from './PlayMoney.scss';
import PlayMoneyCollect from './PlayMoneyCollect/PlayMoneyCollect';

interface Props {
  collectionCallback?: (refilledAmount: number) => void;
  walletIntegration?: boolean;
}

const getPlayChipsMessage = (playChipsData: PlayMoneyRefillInfo, refillTargetReached: boolean) => {
  let message = TXTWCL_You_can_collect_up_to_N0_chips_ELL;
  let calculatedTime = playChipsData?.playChipsRefillPeriodSec;
  const minutes = Math.floor((playChipsData?.playChipsRefillPeriodSec as number) / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 1) {
    message = TXTWCL_You_can_collect_up_to_N0_chips_ELL_2;
    calculatedTime = hours;
  } else if (minutes > 1) {
    message = TXTWCL_You_can_collect_up_to_N0_chips_ELL_1;
    calculatedTime = minutes;
  }

  return refillTargetReached ? (
    <Translation {...TXTWCL_YouXve_reached_the_maximum_amo_ELL} />
  ) : (
    <Translation
      {...message}
      values={{
        0: (
          <CurrencyAmount
            value={playChipsData.playChipsAllowedForPeriodCents as number}
            currency={Currency.PlayMoney}
          />
        ),
        1: calculatedTime
      }}
    />
  );
};

const PlayMoney: React.FC<Props> = ({ collectionCallback, walletIntegration }) => {
  const [refillTime, setRefillTime] = useState(0);
  const [playChipsData, setPlayChipsData] = useState<PlayMoneyRefillInfo>({});
  const [refillTargetMet, setRefillTargetMet] = useState(false);
  const playChips = useSelector(select.account.getPlayChips);
  const isMobile = useSelector(select.device.isMobile);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const { account } = useDispatch<Dispatch>();
  const {
    modal: { enqueue }
  } = useDispatch<Dispatch>();
  const freemium = useFeature(FeatureFlag.Freemium);
  const freemiumMobile = useFeature(FeatureFlag.FreemiumMobile);
  const pmcPaywall = useFeature(FeatureFlag.PmcPaywall);
  const hidePlayMoneyButton = useFeature(FeatureFlag.Wc2HidePlayMoneyButton);
  const request = useRequest();
  const { push } = useHistory();
  const refillTargetReached =
    (playChips &&
      playChipsData.targetRefillAmountCents &&
      playChips >= playChipsData.targetRefillAmountCents) ||
    refillTargetMet;
  const playMoneyCashierTag = pmcPaywall
    ? ResolverTag.PMCPaywall
    : ResolverTag.FreemiumBuyPlayChips;

  useEffect(() => {
    const subscription = request({
      service: '/api/v1-preview/account/play-money-refill'
    }).subscribe({
      next: ({
        body: {
          secondsUntilNextRefillTime,
          playChipsAllowedForPeriodCents,
          playChipsRefillPeriodSec,
          targetRefillAmountCents
        }
      }) => {
        setRefillTime(secondsUntilNextRefillTime);
        setPlayChipsData({
          playChipsAllowedForPeriodCents,
          playChipsRefillPeriodSec,
          targetRefillAmountCents
        });
      },
      error: () => {
        setRefillTime(0);
      }
    });
    return () => subscription.unsubscribe();
  }, [request, playChips]);

  const handleCollect = useCallback(async () => {
    if (refillTime <= 0) {
      const {
        body: { refillResult, refilledAmount }
      } = await account.updatePlayChips();

      if (refillResult === 'ePmRefillSucceeded') {
        if (walletIntegration) {
          collectionCallback?.(refilledAmount || 0);
        } else {
          push({ pathname: '/account/collect-chips', search: encodeQuery({ refilledAmount }) });
        }
      }
      if (refillResult === 'ePmRefillGreaterThanTarget') {
        setRefillTargetMet(true);
      }
    }
  }, [refillTime, account, walletIntegration, collectionCallback, push]);

  const handleBuyChips = useCallback(async () => {
    if (isDesktopEmbedded) {
      window.starsweb?.app.execute(
        `webtokentype-resolve/${playMoneyCashierTag}/${WebTokenType.RATIONAL_WEB_SERVICES}`
      );
    } else {
      enqueue({ path: '/account/cashier/paywall' });
    }
  }, [enqueue, isDesktopEmbedded, playMoneyCashierTag]);

  const showBuyChipsBtn = useMemo(
    () => (isMobile ? freemiumMobile : freemium),
    [freemium, freemiumMobile, isMobile]
  );

  const showCollectChipsBtn = walletIntegration ? !refillTargetReached : true;

  return (
    <section
      className={classNames(styles.buttons, {
        [styles.wallet]: walletIntegration
      })}
    >
      {!hidePlayMoneyButton && showBuyChipsBtn && (
        <StarsButton
          data-testid="buy-chips-button"
          size="small"
          className={styles.button}
          {...(walletIntegration
            ? { onClick: handleBuyChips }
            : { href: '/account/cashier/paywall' })}
          translation={TXTCLI_Buy_Chips}
        />
      )}
      {showCollectChipsBtn && (
        <RawButton
          data-testid="collect-chips-button"
          size="small"
          className={styles.button}
          variant="secondary"
          onClick={handleCollect}
        >
          {refillTime > 0 ? (
            <PlayMoneyCollect refillTime={refillTime} setRefillTime={setRefillTime} />
          ) : (
            <Translation {...TXTWCL_Collect_Chips} />
          )}
        </RawButton>
      )}
      {walletIntegration && playChipsData.playChipsRefillPeriodSec && (
        <p className={playChipsMessage}>
          {getPlayChipsMessage(playChipsData, refillTargetReached)}
        </p>
      )}
    </section>
  );
};

export default PlayMoney;
